import { Focusable } from 'react-aria-components'

import Tooltip from '@components/designSystem/Tooltip'
import TransformableImage from '@components/productImage/TransformableImage'
import { useTooltipPortalContainer } from '@components/shelfDetails/TooltipPortalContext'
import useTypedTranslation from '@hooks/useTypedTranslation.ts'

import type { DOMAttributes } from '@react-types/shared'
import type { ProductWrapper } from '@state/product'
import type { ReactElement } from 'react'

function ProductTooltip({ children, product, gtin, hint }: { children: ReactElement<DOMAttributes, string>; product?: ProductWrapper; gtin?: string | null; hint?: string }) {
  const portalContainer = useTooltipPortalContainer()
  const { t } = useTypedTranslation()

  let tooltipContent

  if (!product && !gtin) {
    tooltipContent = t('storeMap.shelf.unknownFacingHint')
  } else if (!product) {
    tooltipContent = gtin
  } else {
    tooltipContent = (<>
      {
        product.imageUrl && (
          <div className="flex w-full justify-center p-3">
            <TransformableImage
              type="shelfProductImageLarge"
              src={product.imageUrl}
              loadingPlaceholder={<div className="aspect-square w-3/4 animate-pulse rounded-sm bg-gray-600" />}
              className='max-h-100'
            />
          </div>
        )
      }
      <p>{product.name}</p>
      {product.brandName && <p className='font-light'>{product.brandName}</p>}
    </>)
  }

  if (hint) {
    tooltipContent = (<>
      {tooltipContent}
      <p className='font-light'>{hint}</p>
    </>)
  }

  return (
    <Tooltip
      title={tooltipContent}
      arrow={true}
      placement='bottom'
      portalContainer={portalContainer} // we need to specify the portal container to render the tooltip within the shelf container and make hover work correctly
    >
      <Focusable>
        {children}
      </Focusable>
    </Tooltip>
  )
}

export default ProductTooltip